// General
import "./edit-annual-income.scss";
import { useState, useEffect, useRef } from "react";
// Services
import {
  useLazyGetJourneyQuestionsQuery,
  useUpdateEditUserProfileMutation,
} from "../../../../../services/data.service";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  updateAnnualIncome,
  updateIsUnsavedChanges,
} from "../../../../../redux/store/editProfileStore";
import {
  updateProfileCompletionPercentage,
  updateProfileRequirementPopup,
} from "../../../../../redux/store/profileStore";
import { updateUpdateProfileFullscreenDialog } from "../../../../../redux/store/dialogStore";
// Material UI
import { useMediaQuery } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
// i18next
import { useTranslation } from "react-i18next";
// Custom Hooks
import IconManager from "../../../../utility/manager/icon-manager/icon-manager";
// Components
import Spinner from "../../../elements/spinner/spinner";

const EditAnnualIncome = (props) => {
  const { addPaddingTop = false } = props;

  // API variables
  const [
    getJourneyQuestions,
    {
      data: getJourneyQuestionsData,
      error: getJourneyQuestionsErrorData,
      isFetching: getJourneyQuestionsFetching,
      isLoading: getJourneyQuestionsLoading,
      isSuccess: getJourneyQuestionsSuccess,
      isError: getJourneyQuestionsError,
    },
  ] = useLazyGetJourneyQuestionsQuery();
  const [
    updateProfile,
    {
      data: updateProfileData,
      error: updateProfileErrorData,
      isLoading: updateProfileLoading,
      isSuccess: updateProfileSuccess,
      isError: updateProfileError,
    },
  ] = useUpdateEditUserProfileMutation();

  // General variables
  const [localAnnualIncome, setLocalAnnualIncome] = useState(null);
  const [annualIncomeData, setAnnualIncomeData] = useState(null);
  const [annualIncomeIndex, setAnnualIncomeIndex] = useState(null);
  const listRef = useRef(null);

  // Redux variables
  const annualIncome = useSelector((state) => state.editProfile.annualIncome);
  const dispatch = useDispatch();

  // MUI variables
  const isMobile = useMediaQuery("(max-width: 720px)");

  // i18next variables
  const { t } = useTranslation();

  // Custom Hooks Functions
  const getIcon = IconManager();

  // Lifecycle | Mounted
  useEffect(() => {
    setLocalAnnualIncome(annualIncome);

    getJourneyQuestions(null, true);

    return () => {
      listRef.current?.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Lifecycle | Check for update | getJourneyQuestions API Response
  useEffect(() => {
    if (getJourneyQuestionsFetching || getJourneyQuestionsLoading) {
    } else if (getJourneyQuestionsSuccess) {
      if (!annualIncomeData || annualIncomeData?.length <= 0) {
        let annualIncomeData = getJourneyQuestionsData?.selections?.find(
          (item) => item?.category === "annual_income"
        );

        let incomeArrayTemp = JSON.parse(
          JSON.stringify(annualIncomeData?.selections)
        );
        // add empty items to the start and end of the array
        incomeArrayTemp.unshift({ id: 0, display_locale: "", tag: "-" });
        incomeArrayTemp.unshift({ id: 0, display_locale: "", tag: "-" });
        incomeArrayTemp.push({ id: 0, display_locale: "", tag: "-" });
        incomeArrayTemp.push({ id: 0, display_locale: "", tag: "-" });

        setAnnualIncomeData(incomeArrayTemp);
        setAnnualIncomeIndex(
          annualIncome
            ? incomeArrayTemp?.findIndex((item) => item.id === annualIncome?.id)
            : 2
        );
        setLocalAnnualIncome(annualIncome ? annualIncome : incomeArrayTemp[2]);
      }
    } else if (getJourneyQuestionsError) {
    }
  }, [
    getJourneyQuestionsFetching,
    getJourneyQuestionsLoading,
    getJourneyQuestionsSuccess,
    getJourneyQuestionsError,
  ]);

  // Lifecycle | Check for update | updateProfile API Response
  useEffect(() => {
    if (updateProfileLoading) {
    } else if (updateProfileSuccess) {
      dispatch(
        updateAnnualIncome(
          updateProfileData?.data?.user?.occupation?.annual_income
        )
      );
      dispatch(
        updateProfileCompletionPercentage(
          updateProfileData?.data?.user?.profile_progress?.summary?.progress
        )
      );
      dispatch(
        updateProfileRequirementPopup(
          updateProfileData?.data?.user?.profile_progress?.summary?.requirement
        )
      );
      dispatch(updateIsUnsavedChanges(false));
      dispatch(updateUpdateProfileFullscreenDialog(false));
    } else if (updateProfileError) {
    }
  }, [updateProfileLoading, updateProfileSuccess, updateProfileError]);

  // Lifecycle | Check for update | annualIncomeData
  useEffect(() => {
    if (!annualIncomeData || annualIncomeData?.length <= 0) return;

    listRef.current.addEventListener("scroll", onScroll);

    setAnnualIncomeIndex(
      annualIncome
        ? annualIncomeData?.findIndex((item) => item.id === annualIncome?.id)
        : 2
    );
    setLocalAnnualIncome(annualIncome ? annualIncome : annualIncomeData[2]);

    // Scroll to the middle of the selected item
    const itemHeight = listRef.current.firstChild.offsetHeight;
    listRef.current.scrollTop =
      (annualIncome
        ? annualIncomeData?.findIndex((item) => item.id === annualIncome?.id)
        : 2) *
        itemHeight -
      listRef.current.offsetHeight / 2 +
      itemHeight / 2;
  }, [annualIncomeData]);

  // Lifecycle | Check for update | annualIncomeIndex
  useEffect(() => {
    if (!annualIncomeIndex) return;

    // Scroll to the middle of the selected item
    setLocalAnnualIncome(annualIncomeData[annualIncomeIndex]);
  }, [annualIncomeIndex]);

  // Lifecycle | Check for update | localAnnualIncome
  useEffect(() => {
    if (localAnnualIncome?.id !== annualIncome?.id) {
      dispatch(updateIsUnsavedChanges(true));
    } else {
      dispatch(updateIsUnsavedChanges(false));
    }
  }, [localAnnualIncome]);

  // Event Handlers | Button
  const onChangeIncome = (direction) => {
    let newIndex = annualIncomeIndex;

    if (direction === "up") {
      if (annualIncomeIndex > 2) {
        newIndex = annualIncomeIndex - 1;
      }
    } else if (direction === "down") {
      if (annualIncomeIndex < annualIncomeData?.length - 2) {
        newIndex = annualIncomeIndex + 1;
      }
    }

    setAnnualIncomeIndex(newIndex);

    // Scroll to the middle of the selected item
    const itemHeight = listRef.current.firstChild.offsetHeight;
    listRef.current.scrollTop =
      newIndex * itemHeight - listRef.current.offsetHeight / 2 + itemHeight / 2;
  };
  const onSaveAnnualIncome = () => {
    if (!isFormValid()) return;

    const obj = {
      profile: {
        annual_income: localAnnualIncome?.id,
      },
    };
    updateProfile(obj);
  };

  // Utility Functions
  const onScroll = () => {
    const middle = listRef.current.offsetHeight / 2;
    const itemHeight = listRef.current.firstChild.offsetHeight;
    const scrollTop = listRef.current.scrollTop;
    const closestIndex = Math.round((scrollTop + middle) / itemHeight);

    setAnnualIncomeIndex(closestIndex - 1);
  };
  const isFormValid = () => {
    return localAnnualIncome && !updateProfileLoading;
  };

  return (
    <div id="annual-income-subcomponent">
      <div className="padding-container">
        <div
          className={`top-container ${
            addPaddingTop ? "add-edit-padding-top" : ""
          }`}
        >
          <div className="annual-income-icon-container">
            {getIcon("annualIncomeIcon", "annual-income-icon")}
          </div>

          <div className="annual-income-title">
            {t("profile.whats_your_annual_income")}
          </div>

          {getJourneyQuestionsFetching || getJourneyQuestionsLoading ? (
            <Spinner />
          ) : (
            <div className="annual-income-selector-container">
              {!isMobile && <div className="fake-arrow-container"></div>}

              <div className="spacer-container"></div>

              <div className="annual-income-container" ref={listRef}>
                {annualIncomeData?.map((income, index) => (
                  <div
                    className={`income-item ${
                      index === annualIncomeIndex ? "selected" : ""
                    }`}
                    key={index}
                  >
                    {income?.display_locale}
                  </div>
                ))}
              </div>

              <div className="spacer-container"></div>

              {!isMobile && (
                <div className="arrow-container">
                  <ArrowDropUpIcon
                    className="arrow-icon"
                    onClick={() => onChangeIncome("up")}
                  />
                  <ArrowDropDownIcon
                    className="arrow-icon"
                    onClick={() => onChangeIncome("down")}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bottom-container">
        <div
          className={`save-button ${isFormValid() ? "" : "disabled-button"}`}
          onClick={onSaveAnnualIncome}
        >
          {updateProfileLoading ? (
            <Spinner size={19} isPadding={false} color={"white-spinner"} />
          ) : (
            t("common.save")
          )}
        </div>
      </div>
    </div>
  );
};

export default EditAnnualIncome;
